<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      {{
        $router.currentRoute.value.name == "UpdatePodcastAdminShow"
          ? "Update"
          : "Add"
      }}
      Podcast Show
    </h2>

    <!-- Mode Switcher - Only show when adding new show -->
    <div v-if="!showId" class="flex gap-2 mb-4">
      <Button
        type="button"
        :class="[
          'p-button-sm',
          uploadMode === 'single' ? 'p-button-primary' : 'p-button-outlined',
        ]"
        @click="uploadMode = 'single'"
      >
        Single Show
      </Button>
      <Button
        type="button"
        :class="[
          'p-button-sm',
          uploadMode === 'bulk' ? 'p-button-primary' : 'p-button-outlined',
        ]"
        @click="uploadMode = 'bulk'"
      >
        Bulk Upload
      </Button>
    </div>

    <form @submit.prevent>
      <!-- Base Information - Single Mode -->
      <Card v-if="uploadMode === 'single'" class="mb-3">
        <template #title>
          <div class="text-xl">Base Information</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="rss_url">RSS Link *</label>
              <InputText
                id="rss_url"
                v-model="form.rss_url"
                :class="{ 'p-invalid': submitted && !form.rss_url }"
              ></InputText>
              <small v-if="submitted && !form.rss_url" class="p-error">
                RSS link is required
              </small>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1" v-if="showId">
              <label for="show_title">Title</label>
              <InputText id="show_title" v-model="form.show_title"></InputText>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1" v-if="showId">
              <label for="categories">Categories</label>
              <InputText id="categories" v-model="form.categories"></InputText>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="itunes_link">iTunes Link</label>
              <InputText
                id="itunes_link"
                v-model="form.itunes_link"
              ></InputText>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="spotify_link">Spotify Link</label>
              <InputText
                id="spotify_link"
                v-model="form.spotify_link"
              ></InputText>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="avg_per_month">Avg Downloads Per Month *</label>
              <InputText
                id="avg_per_month"
                v-model="form.avg_per_month"
                :class="{ 'p-invalid': submitted && !form.avg_per_month }"
              ></InputText>
              <small v-if="submitted && !form.avg_per_month" class="p-error">
                Avg download per month is required
              </small>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="avg_per_episode">Avg Downloads Per Episode *</label>
              <InputText
                id="avg_per_episode"
                v-model="form.avg_per_episode"
                :class="{ 'p-invalid': submitted && !form.avg_per_episode }"
              ></InputText>
              <small v-if="submitted && !form.avg_per_episode" class="p-error">
                Avg download per episode is required
              </small>
            </div>
          </div>
        </template>
      </Card>

      <!-- Bulk Upload Mode -->
      <Card v-if="uploadMode === 'bulk'" class="mb-3">
        <template #title>
          <div class="text-xl">Bulk Upload Shows</div>
        </template>
        <template #content>
          <div class="flex flex-column gap-3">
            <p class="m-0">Upload CSV file with multiple shows</p>
            <FileUpload
              ref="bulkFileUpload"
              mode="basic"
              accept="text/csv"
              :maxFileSize="1000000"
              @select="onBulkFileSelect"
              customUpload
              :auto="false"
            />
            <small v-if="bulkUploadError" class="p-error">
              {{ bulkUploadError }}
            </small>
            <download-csv :data="sampleBulkCSV">
              <span
                class="text-blue-500 hover:text-blue-300 underline cursor-pointer"
              >
                Download sample CSV file
              </span>
            </download-csv>
            <div v-if="bulkData.length > 0" class="mt-2">
              <p class="font-semibold text-green-600">
                ✓ {{ bulkData.length }} shows loaded successfully
              </p>
            </div>
          </div>
        </template>
      </Card>

      <!-- Show Relations -->
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Show Relations</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="podcaster">Podcaster *</label>
              <Dropdown
                id="podcaster"
                v-model="form.podcaster_id"
                :options="podcasters"
                optionLabel="name"
                optionValue="podcaster_id"
                :filter="true"
                filterBy="name,email"
                placeholder="Select a podcaster"
                :class="{ 'p-invalid': submitted && !form.podcaster_id }"
              >
                <template #option="slotProps">
                  {{ slotProps.option.name }} ({{ slotProps.option.email }})
                </template>
              </Dropdown>
              <small v-if="submitted && !form.podcaster_id" class="p-error">
                Podcaster is required
              </small>
            </div>

            <!-- Network fields - only show if network_admin_name exists -->
            <div
              v-if="form.network_admin_name"
              class="col-12 md:col-6 flex flex-column gap-1"
            >
              <label for="network_admin_name">Network</label>
              <InputText
                id="network_admin_name"
                v-model="form.network_admin_name"
                disabled
              ></InputText>
            </div>
            <div
              v-if="form.network_admin_name"
              class="col-12 md:col-6 flex flex-column gap-1"
            >
              <label for="network_admin_revshare">Network Revshare</label>
              <InputText
                id="network_admin_revshare"
                type="number"
                step="0.01"
                min="0"
                max="1"
                v-model="form.network_admin_revshare"
                :class="{
                  'p-invalid':
                    submitted &&
                    form.network_admin_revshare &&
                    (isNaN(parseFloat(form.network_admin_revshare)) ||
                      parseFloat(form.network_admin_revshare) < 0 ||
                      parseFloat(form.network_admin_revshare) > 1),
                }"
              ></InputText>
              <small
                v-if="
                  submitted &&
                  form.network_admin_revshare &&
                  (isNaN(parseFloat(form.network_admin_revshare)) ||
                    parseFloat(form.network_admin_revshare) < 0 ||
                    parseFloat(form.network_admin_revshare) > 1)
                "
                class="p-error"
              >
                Network Revshare must be a number between 0 and 1
              </small>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1" v-if="showId">
              <label for="author">Author</label>
              <InputText id="author" v-model="form.author"></InputText>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="hosted">Hosted *</label>
              <Dropdown
                id="hosted"
                v-model="form.hosted"
                :options="hostedOptions"
                optionLabel="name"
                optionValue="value"
                placeholder="Select Hosted Type"
                :class="{ 'p-invalid': submitted && !form.hosted }"
              ></Dropdown>
              <small v-if="submitted && !form.hosted" class="p-error">
                Hosted type is required
              </small>
            </div>
          </div>
        </template>
      </Card>

      <Card
        v-if="
          checkHosted(form.hosted, HOSTINGS_SUPPORT_MIDROLLS_LIMIT) && !!showId
        "
        class="mb-3"
      >
        <template #title>
          <div class="text-xl">Ad Insertion Settings</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label for="midrolls_limit">Mid-rolls Limit</label>
              <InputText
                id="midrolls_limit"
                type="number"
                min="0"
                max="6"
                step="1"
                v-model.number="form.midrolls_limit"
              ></InputText>
            </div>
          </div>

          <div v-if="canUseMidrollsInsertOptions" class="grid mt-4">
            <div class="col-12 md:col-6 flex flex-column gap-1 pb-0">
              <label for="first_midroll_position">
                First Mid-roll Position (seconds)
              </label>
              <InputText
                id="first_midroll_position"
                type="number"
                min="40"
                max="160"
                step="1"
                v-model.number="
                  form.midroll_insertion_options.first_midroll_position_seconds
                "
              ></InputText>
            </div>

            <div class="col-12 md:col-6 flex flex-column gap-1 pb-0">
              <label for="first_midroll_markers_limit">
                First Mid-roll Marker Limit
              </label>
              <Dropdown
                id="first_midroll_markers_limit"
                v-model="
                  form.midroll_insertion_options.first_midroll_markers_limit
                "
                :options="[
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                ]"
                optionLabel="label"
                optionValue="value"
                :showClear="false"
              ></Dropdown>
            </div>

            <div
              v-if="showId"
              class="col-12 md:col-6 flex align-items-center gap-2 pb-0"
              style="padding-top: 2.5rem"
            >
              <Checkbox
                id="override_midrolls"
                v-model="
                  form.midroll_insertion_options.override_existing_midrolls
                "
                :binary="true"
              />
              <label for="override_midrolls" class="tw-p-0 tw-m-0">
                Override all existing mid-rolls with these configurations
              </label>
            </div>
          </div>
        </template>
      </Card>

      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Promotion Settings</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label
                for="promotion_pub_date_back_limit"
                class="flex align-items-center gap-2"
              >
                <span>Publication Date Back Limit</span>
                <i
                  class="pi pi-info-circle text-500 cursor-pointer"
                  v-tooltip.bottom="
                    'How many days back from today we can pull episodes for promotions; set 0 to ignore the date filter.'
                  "
                ></i>
              </label>
              <InputText
                id="promotion_pub_date_back_limit"
                type="number"
                v-model="form.promotion_pub_date_back_limit"
              ></InputText>
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label
                for="promotion_last_episodes_limit"
                class="flex align-items-center gap-2"
              >
                <span>Last Episodes Limit</span>
                <i
                  class="pi pi-info-circle text-500 cursor-pointer"
                  v-tooltip.bottom="
                    'Maximum number of the newest episodes eligible for promotions; set 0 to include all episodes.'
                  "
                ></i>
              </label>
              <InputText
                id="promotion_last_episodes_limit"
                type="number"
                v-model="form.promotion_last_episodes_limit"
              ></InputText>
            </div>
          </div>
        </template>
      </Card>

      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Playlist Settings</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label
                for="playlist_pub_date_back_limit"
                class="flex align-items-center gap-2"
              >
                <span>Publication Date Back Limit</span>
                <i
                  class="pi pi-info-circle text-500 cursor-pointer"
                  v-tooltip.bottom="
                    'How many days back from today playlists can pull episodes; set 0 to remove the date limit.'
                  "
                ></i>
              </label>
              <InputText
                id="playlist_pub_date_back_limit"
                type="number"
                v-model="form.playlist_pub_date_back_limit"
              ></InputText>
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-1">
              <label
                for="playlist_last_episodes_limit"
                class="flex align-items-center gap-2"
              >
                <span>Last Episodes Limit</span>
                <i
                  class="pi pi-info-circle text-500 cursor-pointer"
                  v-tooltip.bottom="
                    'Maximum number of the newest episodes to include in playlists; set 0 to include all episodes.'
                  "
                ></i>
              </label>
              <InputText
                id="playlist_last_episodes_limit"
                type="number"
                v-model="form.playlist_last_episodes_limit"
              ></InputText>
            </div>
          </div>
        </template>
      </Card>

      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Listener Apps</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 flex flex-column gap-1">
              <label>Available On</label>
              <div class="p-fluid">
                <MultiSelect
                  v-model="form.listener_apps"
                  :options="listenerAppsOptions"
                  optionLabel="name"
                  optionValue="value"
                  display="chip"
                  placeholder="Select Listener Apps"
                  class="w-full"
                  @change="handleListenerAppsChange"
                />
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Campaigns Section - only show when adding and required fields are filled -->
      <Card v-if="!showId && canShowCampaigns" class="mb-3">
        <template #title>
          <div class="text-xl">Campaigns</div>
        </template>
        <template #content>
          <div class="flex flex-column gap-3">
            <div
              v-for="(campaign, index) in campaigns"
              :key="index"
              class="flex flex-column gap-3 p-3 border-1 border-round surface-50"
              :class="{ 'mb-2': index < campaigns.length - 1 }"
            >
              <div class="flex justify-content-between align-items-center mb-1">
                <h4 class="m-0 text-lg font-semibold">
                  Campaign {{ index + 1 }}
                </h4>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  severity="danger"
                  outlined
                  size="small"
                  @click="removeCampaign(index)"
                />
              </div>
              <!-- Campaign Form Fields using the reusable component -->
              <CampaignFormInputs
                :campaign="campaign"
                @update:campaign="campaigns[index] = $event"
                @calculate-promotions="calculatePromotionsForCampaign(campaign)"
                :countries="countries"
                :sellers="sellers"
                :hasShowDropdown="false"
                :showCalculateButton="uploadMode === 'single'"
                :submitted="submitted"
                :isBulkMode="uploadMode === 'bulk'"
                :showDownloadsBasedOption="true"
              />
            </div>

            <Button
              type="button"
              label="Add Campaign"
              icon="pi pi-plus"
              outlined
              @click="addCampaign"
              class="w-full"
            />
          </div>
        </template>
      </Card>

      <div class="w-full flex justify-content-end gap-2">
        <Button
          class="flex-1 sm:flex-initial"
          outlined
          label="Cancel"
          @click="router.go(-1)"
        />
        <Button
          class="flex-1 sm:flex-initial"
          label="Submit"
          @click="handleSubmit"
        />
      </div>
    </form>

    <!-- Progress Modal -->
    <ApplyMidrollsProgressModal
      :visible="showProgressModal"
      :showTitle="form.show_title"
      :showId="showId"
      :token="token"
      :midrollsLimit="form.midrolls_limit"
      :firstMidrollPosition="
        form.midroll_insertion_options.first_midroll_position_seconds ?? null
      "
      :firstMidrollMarkersLimit="
        form.midroll_insertion_options.first_midroll_markers_limit ?? null
      "
      :overrideExistingMidrolls="
        !!form.midroll_insertion_options.override_existing_midrolls
      "
      @close="closeProgressModal"
      @cancel="cancelProgressModal"
    />
  </div>
</template>

<script setup>
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import Checkbox from "primevue/checkbox";
import FileUpload from "primevue/fileupload";
import CampaignFormInputs from "./CampaignFormInputs.vue";
import ApplyMidrollsProgressModal from "./ApplyMidrollsProgressModal.vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { ref, computed, onMounted, watch } from "vue";
import axios from "axios";
import moment from "moment";
import country from "country-list-js";
import Papa from "papaparse";
import { API_BASE_URL } from "@/consts";

const router = useRouter();
const store = useStore();
const toast = useToast();
const confirm = useConfirm();

const showId = router.currentRoute.value.params.showId;
const submitted = ref(false);
const canApplyMidrolls = ref(false);
const canUseMidrollsInsertOptions = ref(false);
const countries = ref([]);
const uploadMode = ref("single");
const bulkFileUpload = ref();
const bulkUploadError = ref("");
const bulkData = ref([]);
const listenerAppsTouched = ref(false);

const showProgressModal = ref(false);

// Full snapshot of the show as returned by the backend
const originalShow = ref(null);

const HOSTINGS_SUPPORT_MIDROLLS_LIMIT = ["simplecast", "art19", "megaphone"];
const HOSTINGS_SUPPORT_MIDROLL_INSERT_OPTIONS = ["megaphone", "simplecast"];

// Single form data with all fields
const form = ref({
  // Base Information
  rss_url: "",
  show_title: "",
  categories: "",
  itunes_link: "",
  spotify_link: "",
  // Show Base Information
  podcaster_id: "",
  author: "",
  avg_per_episode: 1,
  avg_per_month: 1,
  promotion_pub_date_back_limit: 0,
  promotion_last_episodes_limit: 0,
  playlist_pub_date_back_limit: 0,
  playlist_last_episodes_limit: 0,
  hosted: "",
  listener_apps: [],
  network_admin_name: "",
  network_admin_revshare: null,
  midrolls_limit: showId ? null : 3,
  midroll_insertion_options: {},
});

const campaigns = ref([]);

const sampleBulkCSV = ref([
  {
    rss_url: "https://example.com/feed1.xml",
    itunes_link: "https://podcasts.apple.com/podcast1",
    spotify_link: "https://open.spotify.com/show/podcast1",
    avg_per_month: 10000,
    avg_per_episode: 500,
  },
]);

const listenerAppsOptions = ref([
  { name: "iHeartRadio", value: "iHeartRadio" },
  { name: "Amazon Music", value: "Amazon Music" },
  { name: "Pandora", value: "Pandora" },
  { name: "Spotify", value: "Spotify" },
  { name: "Apple Podcasts", value: "Apple Podcasts" },
  { name: "Spreaker", value: "Spreaker" },
  { name: "Deezer", value: "Deezer" },
  { name: "Overcast", value: "Overcast" },
  { name: "Pocket Casts", value: "Pocket Casts" },
  { name: "Player FM", value: "Player FM" },
  { name: "Goana", value: "Goana" },
  { name: "Podbean", value: "Podbean" },
  { name: "Stitcher", value: "Stitcher" },
  { name: "Audible", value: "Audible" },
]);

const hostedOptions = ref([]);

const token = computed(() =>
  localStorage.getItem(
    router.currentRoute.value.path.includes("podcast-admin/add-show")
      ? "podcastAdminToken"
      : "podcasterToken"
  )
);

const podcasters = ref([]);
const sellers = computed(() => store.getters.sellers || []);

const checkHosted = (hostedValue, allowedTypes) => {
  const type = hostedOptions.value.find((o) => o.value === hostedValue)?.type;
  return type ? allowedTypes.includes(type) : false;
};

const canShowCampaigns = computed(() => {
  if (uploadMode.value === "bulk") {
    return bulkData.value.length > 0;
  }
  // For single mode, require avg_per_month
  return (
    form.value.avg_per_month &&
    !isNaN(parseFloat(form.value.avg_per_month)) &&
    parseFloat(form.value.avg_per_month) > 0
  );
});

const getDefaultListenerApps = (hostValue) =>
  checkHosted(hostValue, ["megaphone"])
    ? ["Apple Podcasts"]
    : ["Apple Podcasts", "Spotify"];

const applyListenerAppsDefault = (hostValue) => {
  if (!hostValue) return;
  form.value.listener_apps = getDefaultListenerApps(hostValue);
};

const handleListenerAppsChange = () => {
  listenerAppsTouched.value = true;
};

const onBulkFileSelect = async (event) => {
  bulkUploadError.value = "";
  bulkData.value = [];

  const file = event.files[0];
  if (!file || file.type !== "text/csv") {
    bulkUploadError.value = "Please select a valid CSV file";
    return;
  }

  try {
    const results = await new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: resolve,
        error: reject,
      });
    });

    if (!results.data || results.data.length === 0) {
      bulkUploadError.value = "CSV file is empty";
      return;
    }

    const requiredFields = ["rss_url", "avg_per_month", "avg_per_episode"];

    const firstRow = results.data[0];
    const missingFields = requiredFields.filter(
      (field) => !(field in firstRow)
    );

    if (missingFields.length > 0) {
      bulkUploadError.value = `Missing required columns: ${missingFields.join(
        ", "
      )}`;
      return;
    }

    const validData = results.data.filter((row) => {
      return (
        row.rss_url &&
        row.rss_url.trim() &&
        row.avg_per_month &&
        !isNaN(parseFloat(row.avg_per_month)) &&
        row.avg_per_episode &&
        !isNaN(parseFloat(row.avg_per_episode))
      );
    });

    if (validData.length === 0) {
      bulkUploadError.value = "No valid show data found in CSV";
      return;
    }

    bulkData.value = validData.map((row) => ({
      rss_url: row.rss_url.trim(),
      categories: row.categories?.trim() || "",
      itunes_link: row.itunes_link?.trim() || "",
      spotify_link: row.spotify_link?.trim() || "",
      avg_per_month: parseFloat(row.avg_per_month),
      avg_per_episode: parseFloat(row.avg_per_episode),
    }));

    toast.add({
      severity: "success",
      summary: "Success",
      detail: `${bulkData.value.length} shows loaded from CSV`,
      life: 3000,
    });
  } catch (error) {
    console.error("Error parsing CSV:", error);
    bulkUploadError.value = "Error parsing CSV file";
  }
};

const calculatePromotionsForCampaign = (campaign) => {
  // Only calculate in single mode
  if (uploadMode.value !== "single") {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "Promotion calculation is only available in single show mode.",
      life: 4000,
    });
    return;
  }

  if (
    !form.value.avg_per_month ||
    isNaN(parseFloat(form.value.avg_per_month))
  ) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "Please enter a valid average listeners per month value first.",
      life: 4000,
    });
    return;
  }

  const avgPerMonth = parseFloat(form.value.avg_per_month);
  const listenersPerHour = avgPerMonth / 30 / 24;
  const promotionsPerInterval =
    listenersPerHour * ((campaign.growth_percentage || 5) / 100);

  campaign.promotions_per_interval = promotionsPerInterval.toFixed(2);
};

const addCampaign = () => {
  const newCampaign = {
    start_date: new Date(moment()),
    end_date: new Date(moment().add(1, "year")),
    geos_wl: ["us"],
    geo_bl: [],
    sellers_wl: [],
    sellers_bl: [],
    promotions_per_interval: 0,
    ctr: 0,
    campaign_type: "promotion",
    status: "active",
    publisher_id: null,
    growth_percentage: 5,
    intervalPolicy: { type: "same" },
    ctrPolicy: { type: "same" },
  };

  // Only calculate for single mode
  if (uploadMode.value === "single") {
    calculatePromotionsForCampaign(newCampaign);
  }

  campaigns.value.push(newCampaign);
};

const removeCampaign = (index) => {
  campaigns.value.splice(index, 1);
};

// Watch if we can use midroll insertion options
watch(
  () => [
    form.value.hosted,
    originalShow.value?.hosting_id,
    hostedOptions.value,
  ],
  ([hosted]) => {
    canUseMidrollsInsertOptions.value =
      !!showId &&
      !!originalShow.value?.hosting_id &&
      checkHosted(hosted, HOSTINGS_SUPPORT_MIDROLL_INSERT_OPTIONS);
  }
);

// Watch midroll-related form fields and decide whether the "Apply Mid-rolls" process can be launched
watch(
  () => [
    form.value.midroll_insertion_options.first_midroll_position_seconds,
    form.value.midroll_insertion_options.first_midroll_markers_limit,
    form.value.midroll_insertion_options.override_existing_midrolls,
    form.value.midrolls_limit,
  ],
  ([posSeconds, markersLimit, overrideExisting, midrollsLimit]) => {
    if (!canUseMidrollsInsertOptions.value) {
      canApplyMidrolls.value = false;
      return;
    }

    const origOpts = originalShow.value?.midroll_insertion_options || {};

    const midrollsConfigIsChanged =
      midrollsLimit !== originalShow.value.midrolls_limit ||
      posSeconds !== (origOpts.first_midroll_position_seconds ?? undefined) ||
      markersLimit !== (origOpts.first_midroll_markers_limit ?? undefined) ||
      !!overrideExisting !== !!origOpts.override_existing_midrolls;

    canApplyMidrolls.value = midrollsConfigIsChanged;
  }
);

// Watch for changes in growth_percentage for any campaign and recalculate (only in single mode)
watch(
  () => campaigns.value.map((c) => c.growth_percentage),
  (newValues, oldValues) => {
    if (uploadMode.value === "single" && newValues && oldValues && !showId) {
      campaigns.value.forEach((campaign, index) => {
        if (newValues[index] !== oldValues[index]) {
          calculatePromotionsForCampaign(campaign);
        }
      });
    }
  },
  { deep: true }
);

// Watch for changes in avg_per_month and recalculate all campaigns (only in single mode)
watch(
  () => form.value.avg_per_month,
  (newValue, oldValue) => {
    if (
      uploadMode.value === "single" &&
      newValue !== oldValue &&
      campaigns.value.length > 0 &&
      !showId
    ) {
      campaigns.value.forEach((campaign) => {
        calculatePromotionsForCampaign(campaign);
      });
    }
  }
);

watch(
  () => form.value.hosted,
  (newHosted, oldHosted) => {
    if (!newHosted || newHosted === oldHosted) {
      return;
    }

    if (listenerAppsTouched.value && form.value.listener_apps?.length) {
      return;
    }

    applyListenerAppsDefault(newHosted);
  }
);

const fetchHostingOptions = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/hosting-platform-seats/options`,
      {
        headers: { Authorization: `Bearer ${token.value}` },
      }
    );

    if (response.data.success) {
      hostedOptions.value = response.data.data;
    }
  } catch (error) {
    console.error("Error fetching hosting options:", error);
    toast.add({
      severity: "error",
      summary: "Failed to fetch hosting options",
      detail: error.message || "An unexpected error occurred",
      life: 3000,
    });
  }
};

const fetchPodcasters = async () => {
  if (router.currentRoute.value.path.includes("podcast-admin/add-show")) {
    try {
      await store.dispatch("loadPodcasters", {
        token: token.value,
        fields: ["podcaster_id", "name", "email", "connected_publisher_id"],
      });
      podcasters.value = store.getters.podcasters.map((podcaster) => ({
        podcaster_id: podcaster.podcaster_id,
        name: podcaster.name,
        email: podcaster.email,
        connected_publisher_id: podcaster.connected_publisher_id,
      }));
    } catch (error) {
      console.error("Error fetching podcasters:", error);
      toast.add({
        severity: "error",
        summary: "Failed to fetch podcasters",
        detail: error.message || "An unexpected error occurred",
        life: 4000,
      });
    }
  }
};

const fetchSellers = () => {
  if (!store.getters.sellers || store.getters.sellers.length === 0) {
    store.dispatch("loadSellers", { token: token.value });
  }
};

const getShowData = async () => {
  try {
    store.commit("setLoading", true);
    const response = await axios.get(
      API_BASE_URL + "/podcaster/shows/" + showId,
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      }
    );
    if (response.data.success) {
      const showData = response.data.show;

      // Store full show' snapshot for later comparisons before updating
      originalShow.value = showData;

      const parsedOptions = JSON.parse(
        JSON.stringify(showData?.midroll_insertion_options || {})
      );

      const existingListenerApps = showData.listener_apps
        ? typeof showData.listener_apps === "string"
          ? JSON.parse(showData.listener_apps)
          : showData.listener_apps
        : [];

      listenerAppsTouched.value = existingListenerApps.length > 0;

      // Populate form data directly
      Object.assign(form.value, {
        rss_url: showData.rss_url,
        show_title: showData.show_title,
        categories: showData.categories,
        itunes_link: showData.itunes_link,
        spotify_link: showData.spotify_link,
        podcaster_id: showData.podcaster_id,
        author: showData.author,
        avg_per_episode: showData.avg_per_episode,
        avg_per_month: showData.avg_per_month,
        promotion_pub_date_back_limit: showData.promotion_pub_date_back_limit,
        promotion_last_episodes_limit: showData.promotion_last_episodes_limit,
        playlist_pub_date_back_limit: showData.playlist_pub_date_back_limit,
        playlist_last_episodes_limit: showData.playlist_last_episodes_limit,
        hosted: showData.hosted,
        listener_apps: existingListenerApps,
        network_admin_name: showData.network_admin_name || "",
        network_admin_revshare: showData.network_admin_revshare,
        midrolls_limit: showData.midrolls_limit,
        midroll_insertion_options: parsedOptions,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Show data not found",
        life: 4000,
      });
    }
  } catch (error) {
    console.error(error);
    toast.add({
      severity: "error",
      summary: "Something went wrong...",
      life: 4000,
    });
  } finally {
    store.commit("setLoading", false);
  }
};

// Helper function to validate campaigns
const validateCampaigns = () => {
  if (campaigns.value.length === 0) {
    return true;
  }

  for (let i = 0; i < campaigns.value.length; i++) {
    const campaign = campaigns.value[i];

    if (campaign.start_date >= campaign.end_date) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: `Campaign ${i + 1}: End date must be later than start date`,
        life: 4000,
      });
      return false;
    }

    if (campaign.campaign_type === "publisher" && !campaign.publisher_id) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: `Campaign ${
          i + 1
        }: Publisher ID is required for publisher campaigns`,
        life: 4000,
      });
      return false;
    }

    if (uploadMode.value === "bulk" && campaign.ctrPolicy) {
      const policy = campaign.ctrPolicy;

      if (policy.type === "random") {
        if (!policy.min || !policy.max) {
          toast.add({
            severity: "warn",
            summary: "Warning",
            detail: `Campaign ${
              i + 1
            }: Min and Max CTR values are required for random CTR mode`,
            life: 4000,
          });
          return false;
        }
        if (policy.min > policy.max) {
          toast.add({
            severity: "warn",
            summary: "Warning",
            detail: `Campaign ${
              i + 1
            }: Min CTR value cannot be greater than Max CTR value`,
            life: 4000,
          });
          return false;
        }
      }
    }

    if (
      campaign.ctrPolicy?.type !== "random" &&
      (!campaign.ctr || parseFloat(campaign.ctr) <= 0)
    ) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: `Campaign ${i + 1}: CTR is required`,
        life: 4000,
      });
      return false;
    }

    if (
      campaign.intervalPolicy?.type !== "random" &&
      campaign.intervalPolicy?.type !== "avgDownloadsBased" &&
      campaign.status !== "Mellow mode" &&
      (!campaign.promotions_per_interval ||
        parseFloat(campaign.promotions_per_interval) <= 0)
    ) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: `Campaign ${
          i + 1
        }: Promotions Per Interval must be greater than 0`,
        life: 4000,
      });
      return false;
    }

    if (uploadMode.value === "bulk" && campaign.intervalPolicy) {
      const policy = campaign.intervalPolicy;

      if (policy.type === "random") {
        if (!policy.min || !policy.max) {
          toast.add({
            severity: "warn",
            summary: "Warning",
            detail: `Campaign ${
              i + 1
            }: Min and Max values are required for random interval mode`,
            life: 4000,
          });
          return false;
        }
        if (policy.min > policy.max) {
          toast.add({
            severity: "warn",
            summary: "Warning",
            detail: `Campaign ${
              i + 1
            }: Min value cannot be greater than Max value`,
            life: 4000,
          });
          return false;
        }
      }
    }
  }
  return true;
};

const handleSubmit = () => {
  submitted.value = true;

  // Bulk mode validation
  if (uploadMode.value === "bulk") {
    if (bulkData.value.length === 0) {
      toast.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Please upload a CSV file with show data",
        life: 4000,
      });
      return;
    }

    if (!form.value.hosted || !form.value.podcaster_id) {
      toast.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all required fields",
        life: 4000,
      });
      return;
    }
    if (!validateCampaigns()) return;
    submitForm({ rssChanged: false });
    return;
  }

  // Single mode validation
  if (!form.value.rss_url) {
    toast.add({
      severity: "error",
      summary: "Validation Error",
      detail: "RSS link is required",
      life: 4000,
    });
    return;
  }

  // Validate Promotion Settings values
  if (
    isNaN(form.value.promotion_pub_date_back_limit) ||
    isNaN(form.value.promotion_last_episodes_limit) ||
    form.value.promotion_pub_date_back_limit < 0 ||
    form.value.promotion_last_episodes_limit < 0
  ) {
    toast.add({
      severity: "error",
      summary: "Validation Error",
      detail: "Promotion settings values must be numbers and 0 or higher.",
      life: 4000,
    });
    return;
  }

  // Validate Playlist Settings values
  if (
    isNaN(form.value.playlist_pub_date_back_limit) ||
    isNaN(form.value.playlist_last_episodes_limit) ||
    form.value.playlist_pub_date_back_limit < 0 ||
    form.value.playlist_last_episodes_limit < 0
  ) {
    toast.add({
      severity: "error",
      summary: "Validation Error",
      detail: "Playlist settings values must be numbers and 0 or higher.",
      life: 4000,
    });
    return;
  }

  // Validate Network Revshare if provided
  if (form.value.network_admin_name && form.value.network_admin_revshare) {
    const revshare = parseFloat(form.value.network_admin_revshare);
    if (isNaN(revshare) || revshare < 0 || revshare > 1) {
      toast.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Network Revshare must be a number between 0 and 1.",
        life: 4000,
      });
      return;
    }
  }

  if (!validateCampaigns()) {
    return;
  }

  if (
    form.value.avg_per_episode &&
    form.value.avg_per_month &&
    form.value.hosted &&
    form.value.podcaster_id
  ) {
    const rssChanged =
      !!showId && form.value.rss_url !== originalShow.value?.rss_url;

    if (rssChanged) {
      confirm.require({
        message:
          "You changed the RSS feed URL. This will cause migration of the show and delete all previous episodes. Are you sure?",
        header: "Confirm RSS Change",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => submitForm({ rssChanged: true }),
        reject: () => {
          submitted.value = false;
        },
      });
    } else {
      submitForm({ rssChanged: false });
    }
  }
};

const closeProgressModal = () => {
  showProgressModal.value = false;
  router.replace({ name: "PodcastAdminShowsList" });
};

const cancelProgressModal = () => {
  showProgressModal.value = false;
  router.replace({ name: "PodcastAdminShowsList" });
};

const submitForm = async ({ rssChanged = false } = {}) => {
  try {
    store.commit("setLoading", true);

    const formattedCampaigns =
      campaigns.value.length > 0
        ? campaigns.value.map((campaign) => {
            const campaignData = {
              start_date: campaign.start_date.toISOString(),
              end_date: campaign.end_date.toISOString(),
              geos_wl: campaign.geos_wl?.join() || "",
              geo_bl: campaign.geo_bl?.join() || "",
              sellers_wl: campaign.sellers_wl?.join() || "",
              sellers_bl: campaign.sellers_bl?.join() || "",
              campaign_type: campaign.campaign_type,
              status: campaign.status,
              publisher_id: campaign.publisher_id || "",
              growth_percentage: campaign.growth_percentage || 5,
            };

            // Handle CTR policy for bulk mode
            if (
              uploadMode.value === "bulk" &&
              campaign.ctrPolicy?.type !== "same"
            ) {
              campaignData.ctrPolicy = campaign.ctrPolicy;
            } else {
              // Single mode or "same for all" in bulk mode
              campaignData.ctr = campaign.ctr;
            }

            // Handle interval policy for bulk mode
            if (
              uploadMode.value === "bulk" &&
              campaign.intervalPolicy?.type !== "same"
            ) {
              campaignData.intervalPolicy = campaign.intervalPolicy;
            } else {
              // Single mode or "same interval for all" in bulk mode - always include promotions_per_interval value
              campaignData.promotions_per_interval =
                campaign.promotions_per_interval;
            }

            return campaignData;
          })
        : [];

    const payload = {
      podcaster_id: form.value.podcaster_id,
      author: form.value.author,
      promotion_pub_date_back_limit: form.value.promotion_pub_date_back_limit,
      promotion_last_episodes_limit: form.value.promotion_last_episodes_limit,
      playlist_pub_date_back_limit: form.value.playlist_pub_date_back_limit,
      playlist_last_episodes_limit: form.value.playlist_last_episodes_limit,
      hosted: form.value.hosted,
      listener_apps: form.value.listener_apps || [],
      network_admin_revshare: form.value.network_admin_revshare
        ? parseFloat(form.value.network_admin_revshare)
        : null,
      midrolls_limit:
        form.value.midrolls_limit && form.value.midrolls_limit >= 0
          ? form.value.midrolls_limit
          : null,
      midroll_insertion_options: canUseMidrollsInsertOptions.value
        ? {
            ...(form.value.midroll_insertion_options
              .first_midroll_position_seconds
              ? {
                  first_midroll_position_seconds:
                    form.value.midroll_insertion_options
                      .first_midroll_position_seconds,
                }
              : {}),
            ...(form.value.midroll_insertion_options.first_midroll_markers_limit
              ? {
                  first_midroll_markers_limit:
                    form.value.midroll_insertion_options
                      .first_midroll_markers_limit,
                }
              : {}),
            override_existing_midrolls:
              !!form.value.midroll_insertion_options.override_existing_midrolls,
          }
        : null,
    };

    // Add bulk data or single show data
    if (uploadMode.value === "bulk") {
      payload.bulkData = bulkData.value;
    } else {
      payload.rss_url = form.value.rss_url;
      payload.show_title = form.value.show_title;
      payload.categories = form.value.categories;
      payload.itunes_link = form.value.itunes_link;
      payload.spotify_link = form.value.spotify_link;
      payload.avg_per_episode = form.value.avg_per_episode;
      payload.avg_per_month = form.value.avg_per_month;
    }

    if (showId) {
      payload.show_id = showId;
      payload.rss_changed = rssChanged;
    }

    if (formattedCampaigns.length > 0) {
      payload.campaigns = formattedCampaigns;
    }

    const response = await axios.post(
      API_BASE_URL + "/podcaster/add-show",
      payload,
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      }
    );

    if (response.data?.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: showId
          ? "Show updated successfully!"
          : uploadMode.value === "bulk"
          ? `${bulkData.value.length} shows added successfully!`
          : "Show added successfully!",
        life: 4000,
      });

      if (canApplyMidrolls.value) {
        showProgressModal.value = true;
      } else {
        router.replace({ name: "PodcastAdminShowsList" });
      }
    } else {
      toast.add({
        severity: "error",
        detail: response.data?.message || "Something went wrong...",
        life: 4000,
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Something went wrong...",
      life: 4000,
    });
    console.log(error);
  } finally {
    store.commit("setLoading", false);
  }
};

watch(
  () => campaigns.value,
  (newCampaigns) => {
    newCampaigns.forEach((campaign) => {
      if (campaign.status === "Mellow mode") {
        campaign.promotions_per_interval = 0;
        campaign.ctr = 0.18;
      }
    });
  },
  { deep: true }
);

onMounted(() => {
  countries.value = country.names().map((name) => {
    const data = country.findByName(name);
    return { name, geo: data.code.iso2.toLowerCase() };
  });

  fetchHostingOptions();
  fetchPodcasters();
  fetchSellers();

  if (showId) {
    getShowData();
  }
});
</script>

<style scoped>
.p-inputnumber,
.p-inputtext,
.p-inputgroup {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 0.5rem;
}

input {
  max-width: 100%;
}
</style>
